import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, formatMoney, formatApiErrorDetail, resolveImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PUBLIC_EVENT, PUBLIC_EVENT_EXTRA } from "@/constants/testIds";
import { Calendar, MapPin, Minus, Plus, Lock, Tag, X, KeyRound, Clock } from "lucide-react";

const BOOKING_FEE_PENCE = 195;

function formatWhen(iso) {
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "full", timeStyle: "short" }); }
  catch { return iso; }
}

export default function PublicEvent() {
  const { slug, eventId } = useParams();
  const [data, setData] = useState(null);
  const [salesOpen, setSalesOpen] = useState(true);
  const [err, setErr] = useState("");
  const [qty, setQty] = useState({});
  const [buyer, setBuyer] = useState({ name: "", email: "" });
  const [answers, setAnswers] = useState({});
  const [accessCode, setAccessCode] = useState("");
  const [accessGranted, setAccessGranted] = useState(false);
  const [accessError, setAccessError] = useState("");
  // Multi-code, per-tier access: as buyer enters valid codes, we accumulate unlocked tier ids.
  const [unlockedTierIds, setUnlockedTierIds] = useState([]);
  const [enteredCodes, setEnteredCodes] = useState([]);       // list of validated codes
  const [showAnotherCode, setShowAnotherCode] = useState(false);
  // Members-only gate
  const [memberEmail, setMemberEmail] = useState("");
  const [memberVerified, setMemberVerified] = useState(false);
  const [memberError, setMemberError] = useState("");
  const [memberTierLabel, setMemberTierLabel] = useState(null);
  const [discountCode, setDiscountCode] = useState("");
  const [discountApplied, setDiscountApplied] = useState(null); // {code, discount_pence}
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/public/events/${eventId}`);
      setData(data);
      setSalesOpen(data.sales_open !== false);
      if (!data.event.access_code_required) setAccessGranted(true);
    } catch (e) {
      setErr("Event not found or unavailable");
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    if (!data) return { subtotal: 0, fee: 0, total: 0, totalQty: 0, discount: 0 };
    let subtotal = 0, totalQty = 0;
    for (const t of data.event.tiers) {
      const q = qty[t.id] || 0;
      subtotal += q * t.price_pence;
      totalQty += q;
    }
    const discount = discountApplied ? Math.min(discountApplied.discount_pence, subtotal) : 0;
    // If venue hid the fee, ticket prices already include the +£2 uplift — don't add fee line again.
    const hideFee = !!data.event.hide_booking_fee;
    const fee = hideFee ? 0 : totalQty * BOOKING_FEE_PENCE;
    return { subtotal, fee, total: subtotal - discount + fee, totalQty, discount, hideFee };
  }, [data, qty, discountApplied]);

  const inc = (tier) => {
    const remaining = tier.quantity - (tier.sold || 0);
    setQty((q) => ({ ...q, [tier.id]: Math.min((q[tier.id] || 0) + 1, remaining) }));
  };
  const dec = (tier) => setQty((q) => ({ ...q, [tier.id]: Math.max((q[tier.id] || 0) - 1, 0) }));

  const submitAccessCode = async (e) => {
    e.preventDefault();
    setAccessError("");
    const code = (accessCode || "").trim();
    if (!code) { setAccessError("Enter a code"); return; }
    try {
      const { data } = await axios.post(`${API}/public/events/${eventId}/access-code`, { code });
      // New backend returns { ok, unlocked_tier_ids }; old shape returned just { ok }
      const newlyUnlocked = Array.isArray(data?.unlocked_tier_ids) ? data.unlocked_tier_ids : [];
      setUnlockedTierIds((prev) => Array.from(new Set([...(prev || []), ...newlyUnlocked])));
      setEnteredCodes((prev) => prev.includes(code) ? prev : [...prev, code]);
      setAccessGranted(true);
      setAccessCode("");
      setShowAnotherCode(false);
    } catch (err) {
      setAccessError("Wrong code. Try again.");
    }
  };

  const applyDiscount = async () => {
    if (!discountCode.trim() || totals.subtotal === 0) {
      toast.error(totals.subtotal === 0 ? "Select tickets first" : "Enter a code");
      return;
    }
    try {
      const { data: resp } = await axios.post(`${API}/public/events/${eventId}/discount`, {
        code: discountCode,
        subtotal_pence: totals.subtotal,
      });
      setDiscountApplied({ code: resp.code, discount_pence: resp.discount_pence, kind: resp.kind, amount: resp.amount });
      toast.success(`Discount ${resp.code} applied`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid discount code");
    }
  };

  const checkout = async () => {
    if (totals.totalQty === 0) { toast.error("Select at least 1 ticket"); return; }
    if (!buyer.name || !buyer.email) { toast.error("Enter your name and email"); return; }
    // Validate required custom questions client-side
    for (const q of data.event.custom_questions || []) {
      if (q.required && !answers[q.id]) {
        toast.error(`Please answer: ${q.label}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const items = Object.entries(qty)
        .filter(([, q]) => q > 0)
        .map(([tier_id, q]) => {
          const t = data.event.tiers.find((x) => x.id === tier_id);
          return { tier_id, tier_name: t.name, quantity: q, unit_price_pence: t.price_pence };
        });
      const { data: resp } = await axios.post(`${API}/checkout/tickets`, {
        event_id: eventId,
        items,
        buyer_email: buyer.email,
        buyer_name: buyer.name,
        origin_url: window.location.origin,
        access_code: accessCode || null,
        access_codes: enteredCodes,
        discount_code: discountApplied ? discountApplied.code : null,
        answers,
      });
      window.location.href = resp.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout error");
      setSubmitting(false);
    }
  };

  if (err) return <div className="min-h-screen grid place-items-center text-zinc-500">{err}</div>;
  if (!data) return <div className="min-h-screen grid place-items-center text-zinc-500 text-sm">Loading…</div>;

  const { event, venue } = data;
  const brand = venue?.brand_color || "#4338CA";
  const eventImg = resolveImageUrl(event.image_url);
  const venueLogo = resolveImageUrl(venue?.logo_url);

  const submitMembership = async (e) => {
    e.preventDefault();
    setMemberError("");
    if (!memberEmail.trim()) { setMemberError("Enter your email"); return; }
    try {
      const { data } = await axios.post(`${API}/public/events/${eventId}/verify-membership`, { email: memberEmail.trim() });
      setMemberVerified(true);
      setMemberTierLabel(data.tier_label);
      // Pre-fill buyer email so checkout uses the verified members-only address
      setBuyer((b) => ({ ...b, email: memberEmail.trim() }));
    } catch (err) {
      setMemberError("No active membership on this email. Sign up first, or use a different email.");
    }
  };

  // MEMBERS-ONLY GATE (shown before access code gate — membership is the outer wall)
  if (event.members_only && !memberVerified) {
    return (
      <div className="min-h-screen bg-white text-zinc-900" data-testid="members-only-gate">
        <header className="border-b border-zinc-200">
          <div className="max-w-6xl mx-auto px-6 py-5 flex items-center gap-3">
            {venueLogo ? (
              <img src={venueLogo} alt={venue.name} className="h-9 w-9 object-contain rounded-md" />
            ) : (
              <div className="h-9 w-9 grid place-items-center rounded-md text-white font-display font-black text-sm" style={{ background: brand }}>
                {venue?.name?.[0] || "V"}
              </div>
            )}
            <div className="font-display font-black text-lg tracking-tight">{venue?.name}</div>
          </div>
        </header>
        <main className="max-w-md mx-auto px-6 py-16">
          <div className="text-xs uppercase tracking-widest font-mono" style={{ color: brand }}>Members only</div>
          <h1 className="mt-2 font-display font-black text-3xl">{event.title}</h1>
          <p className="mt-3 text-sm text-zinc-600">This event is open to active members only. Enter the email address you signed up with to verify.</p>
          <form onSubmit={submitMembership} className="mt-6 space-y-3">
            <Input
              type="email"
              value={memberEmail}
              onChange={(e) => setMemberEmail(e.target.value)}
              placeholder="your@email.com"
              autoFocus
              data-testid="members-only-email"
              className="h-11"
              required
            />
            {memberError && <div className="text-sm text-red-600" data-testid="members-only-error">{memberError}</div>}
            <Button type="submit" data-testid="members-only-submit" className="w-full h-11 rounded-full text-white" style={{ background: brand }}>
              Verify membership
            </Button>
          </form>
          <div className="mt-4 text-xs text-zinc-500">
            Not a member yet? <a href={`/v/${venue?.slug}/membership`} className="underline">Join first →</a>
          </div>
        </main>
      </div>
    );
  }

  // ACCESS CODE GATE
  if (event.access_code_required && !accessGranted) {
    return (
      <div className="min-h-screen bg-white text-zinc-900" data-testid={PUBLIC_EVENT.root}>
        <header className="border-b border-zinc-200">
          <div className="max-w-6xl mx-auto px-6 py-5 flex items-center gap-3">
            {venueLogo ? (
              <img src={venueLogo} alt={venue.name} className="h-9 w-9 object-contain rounded-md" />
            ) : (
              <div className="h-9 w-9 grid place-items-center rounded-md text-white font-display font-black text-sm" style={{ background: brand }}>
                {venue?.name?.[0] || "V"}
              </div>
            )}
            <div className="font-display font-black text-lg tracking-tight">{venue?.name}</div>
          </div>
        </header>
        <main className="max-w-md mx-auto px-6 py-24">
          <div className="rounded-2xl border border-zinc-200 bg-white p-8 text-center">
            <div className="mx-auto w-14 h-14 rounded-full grid place-items-center text-white" style={{ background: brand }}>
              <KeyRound className="w-6 h-6" />
            </div>
            <h1 className="mt-5 font-display font-black text-3xl tracking-tighter">Private event</h1>
            <p className="mt-2 text-zinc-500 text-sm">Enter your access code to view tickets.</p>
            <form onSubmit={submitAccessCode} className="mt-6 space-y-3">
              <Input
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                placeholder="Access code"
                data-testid={PUBLIC_EVENT_EXTRA.accessCodeInput}
                className="h-11 text-center font-mono tracking-wider"
                autoFocus
              />
              {accessError && (
                <div className="text-sm text-red-600" data-testid={PUBLIC_EVENT_EXTRA.accessCodeError}>{accessError}</div>
              )}
              <Button type="submit" data-testid={PUBLIC_EVENT_EXTRA.accessCodeSubmit} className="w-full h-11 rounded-full text-white" style={{ background: brand }}>
                Unlock
              </Button>
            </form>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid={PUBLIC_EVENT.root}>
      <header className="border-b border-zinc-200" data-testid={PUBLIC_EVENT.venueHeader}>
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center gap-3">
          {venueLogo ? (
            <img src={venueLogo} alt={venue.name} className="h-9 w-9 object-contain rounded-md" />
          ) : (
            <div className="h-9 w-9 grid place-items-center rounded-md text-white font-display font-black text-sm" style={{ background: brand }}>
              {venue?.name?.[0] || "V"}
            </div>
          )}
          <div className="font-display font-black text-lg tracking-tight">{venue?.name}</div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-5 gap-10">
        {/* Event details */}
        <div className="lg:col-span-3">
          {eventImg && (
            <div className="rounded-xl overflow-hidden aspect-[16/9] mb-6 bg-zinc-100">
              <img src={eventImg} alt={event.title} className="w-full h-full object-cover" />
            </div>
          )}
          <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-tight">{event.title}</h1>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-zinc-600">
            <div className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {formatWhen(event.start_at)}</div>
            {event.venue_location && (
              <div className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {event.venue_location}</div>
            )}
          </div>
          {event.description && (
            <div className="mt-6 whitespace-pre-line text-zinc-700 leading-relaxed">{event.description}</div>
          )}
          <EventLocationMap
            location={event.venue_location}
            lat={event.venue_location_lat}
            lon={event.venue_location_lon}
          />
          {event.is_recurring && event.recurrence_rule && (
            <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-zinc-200 text-xs font-medium">
              Recurring · {event.recurrence_rule}
            </div>
          )}
          {(event.sales_start_at || event.sales_end_at) && (
            <div className="mt-4 flex items-center gap-1.5 text-xs text-zinc-500">
              <Clock className="w-3.5 h-3.5" />
              {event.sales_start_at && `Sales opened ${formatWhen(event.sales_start_at)}. `}
              {event.sales_end_at && `Sales close ${formatWhen(event.sales_end_at)}.`}
            </div>
          )}
        </div>

        {/* Ticket sidebar */}
        <aside className="lg:col-span-2">
          <div className="rounded-xl border border-zinc-200 bg-white p-6 sticky top-8">
            <h2 className="font-display font-bold text-lg">Get tickets</h2>

            {!salesOpen && (
              <div className="mt-4 rounded-lg bg-zinc-900 text-white p-4 text-sm" data-testid={PUBLIC_EVENT_EXTRA.saleClosedBanner}>
                Ticket sales are currently closed for this event.
              </div>
            )}

            <div className="mt-4 space-y-3">
              {event.members_only && memberVerified && memberTierLabel && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800" data-testid="members-only-verified-banner">
                  Verified as <strong>{memberTierLabel}</strong> member ({memberEmail})
                </div>
              )}
              {(() => {
                // If the event has access codes at all, filter tiers to only what buyer's codes unlocked.
                const gated = !!event.access_code_required;
                const visibleTiers = gated
                  ? event.tiers.filter((t) => unlockedTierIds.includes(t.id))
                  : event.tiers;
                return visibleTiers;
              })().map((t) => {
                const remaining = t.quantity - (t.sold || 0);
                const disabled = remaining <= 0 || !salesOpen;
                return (
                  <div key={t.id} className="rounded-lg border border-zinc-200 p-4" data-testid={PUBLIC_EVENT.tierRow(t.id)}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-semibold">{t.name}</div>
                        <div className="text-sm text-zinc-500">{formatMoney(t.price_pence)} · {remaining} left</div>
                        {t.description && <div className="mt-1 text-xs text-zinc-500">{t.description}</div>}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="icon" disabled={disabled || (qty[t.id] || 0) <= 0} onClick={() => dec(t)} data-testid={PUBLIC_EVENT.qtyDec(t.id)} className="h-8 w-8 rounded-full">
                          <Minus className="w-3.5 h-3.5" />
                        </Button>
                        <div className="w-7 text-center font-mono font-semibold" data-testid={PUBLIC_EVENT.qtyValue(t.id)}>{qty[t.id] || 0}</div>
                        <Button size="icon" disabled={disabled} onClick={() => inc(t)} data-testid={PUBLIC_EVENT.qtyInc(t.id)} className="h-8 w-8 rounded-full text-white" style={{ background: brand }}>
                          <Plus className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Have another access code? (only if event is code-gated and buyer already unlocked something) */}
            {event.access_code_required && accessGranted && (
              <div className="mt-3" data-testid="another-access-code-block">
                {!showAnotherCode ? (
                  <button
                    type="button"
                    onClick={() => setShowAnotherCode(true)}
                    data-testid="another-access-code-toggle"
                    className="text-sm text-zinc-600 underline underline-offset-2 hover:text-zinc-900"
                  >
                    Have another access code?
                  </button>
                ) : (
                  <form onSubmit={submitAccessCode} className="flex gap-2 items-start">
                    <Input
                      value={accessCode}
                      onChange={(e) => setAccessCode(e.target.value)}
                      placeholder="Enter another code"
                      data-testid="another-access-code-input"
                      className="h-10 font-mono"
                      autoFocus
                    />
                    <Button type="submit" size="sm" data-testid="another-access-code-submit" className="h-10 rounded-full" style={{ background: brand, color: "white" }}>
                      Unlock
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => { setShowAnotherCode(false); setAccessError(""); setAccessCode(""); }} className="h-10">
                      Cancel
                    </Button>
                  </form>
                )}
                {accessError && showAnotherCode && (
                  <div className="mt-1 text-xs text-red-600">{accessError}</div>
                )}
                {enteredCodes.length > 0 && (
                  <div className="mt-2 text-xs text-zinc-500">
                    Codes applied: {enteredCodes.map((c) => <code key={c} className="font-mono ml-1">{c}</code>)}
                  </div>
                )}
              </div>
            )}

            {/* Discount code */}
            {totals.totalQty > 0 && salesOpen && (
              <div className="mt-4">
                {discountApplied ? (
                  <div className="flex items-center justify-between rounded-lg border border-emerald-300 bg-emerald-50 p-3" data-testid={PUBLIC_EVENT_EXTRA.discountApplied}>
                    <div className="flex items-center gap-2 text-sm">
                      <Tag className="w-4 h-4 text-emerald-700" />
                      <div>
                        <div className="font-semibold text-emerald-900">{discountApplied.code}</div>
                        <div className="text-xs text-emerald-800">−{formatMoney(discountApplied.discount_pence)}</div>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => { setDiscountApplied(null); setDiscountCode(""); }} data-testid={PUBLIC_EVENT_EXTRA.discountRemove} className="h-8 w-8">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input value={discountCode} onChange={(e) => setDiscountCode(e.target.value.toUpperCase())} placeholder="Discount code" data-testid={PUBLIC_EVENT_EXTRA.discountInput} className="h-10 font-mono" />
                    <Button variant="outline" onClick={applyDiscount} data-testid={PUBLIC_EVENT_EXTRA.discountApply} className="rounded-full">Apply</Button>
                  </div>
                )}
              </div>
            )}

            {totals.totalQty > 0 && (
              <div className="mt-5 pt-5 border-t border-zinc-200 space-y-1 text-sm">
                <div className="flex justify-between text-zinc-600"><span>Subtotal</span><span>{formatMoney(totals.subtotal)}</span></div>
                {totals.discount > 0 && (
                  <div className="flex justify-between text-emerald-700"><span>Discount</span><span>−{formatMoney(totals.discount)}</span></div>
                )}
                {!totals.hideFee && (
                  <div className="flex justify-between text-zinc-600"><span>Booking fee ({totals.totalQty} × £1.95)</span><span>{formatMoney(totals.fee)}</span></div>
                )}
                <div className="flex justify-between font-semibold text-base pt-2 border-t border-zinc-200 mt-2"><span>Total</span><span>{formatMoney(totals.total)}</span></div>
              </div>
            )}

            {/* Buyer details */}
            <div className="mt-5 space-y-3">
              <div>
                <Label>Your name</Label>
                <Input value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} data-testid={PUBLIC_EVENT.buyerName} className="mt-2 h-11" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} data-testid={PUBLIC_EVENT.buyerEmail} className="mt-2 h-11" />
              </div>

              {(event.custom_questions || []).map((q) => (
                <div key={q.id}>
                  <Label>{q.label}{q.required && <span className="text-red-500 ml-1">*</span>}</Label>
                  {q.type === "text" && (
                    <Input value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} data-testid={PUBLIC_EVENT_EXTRA.answer(q.id)} className="mt-2 h-11" />
                  )}
                  {q.type === "email" && (
                    <Input type="email" value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} data-testid={PUBLIC_EVENT_EXTRA.answer(q.id)} className="mt-2 h-11" />
                  )}
                  {q.type === "phone" && (
                    <Input type="tel" value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} data-testid={PUBLIC_EVENT_EXTRA.answer(q.id)} className="mt-2 h-11" />
                  )}
                  {q.type === "select" && (
                    <Select value={answers[q.id] || ""} onValueChange={(v) => setAnswers({ ...answers, [q.id]: v })}>
                      <SelectTrigger className="mt-2 h-11" data-testid={PUBLIC_EVENT_EXTRA.answer(q.id)}><SelectValue placeholder="Choose…" /></SelectTrigger>
                      <SelectContent>
                        {(q.options || []).map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {q.type === "checkbox" && (
                    <div className="mt-2 flex items-center gap-2">
                      <Checkbox id={`q-${q.id}`} checked={!!answers[q.id]} onCheckedChange={(v) => setAnswers({ ...answers, [q.id]: v ? "yes" : "" })} data-testid={PUBLIC_EVENT_EXTRA.answer(q.id)} />
                      <Label htmlFor={`q-${q.id}`} className="cursor-pointer text-sm">Yes</Label>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <Button
              onClick={checkout}
              disabled={submitting || totals.totalQty === 0 || !salesOpen}
              className="mt-5 w-full h-12 rounded-full text-white font-semibold hover:opacity-90 disabled:opacity-50"
              style={{ background: brand }}
              data-testid={PUBLIC_EVENT.checkoutBtn}
            >
              <Lock className="w-4 h-4 mr-2" />
              {submitting ? "Redirecting to Stripe…" : (!salesOpen ? "Sales closed" : totals.totalQty === 0 ? "Select tickets" : `Pay ${formatMoney(totals.total)}`)}
            </Button>
            <div className="mt-3 text-xs text-zinc-500 text-center">Secured by Stripe. Powered by Affra.</div>
            <div className="mt-2 text-xs text-zinc-500 text-center">
              By continuing you agree to{" "}
              <a href={`/v/${slug}/terms`} target="_blank" rel="noreferrer" className="underline hover:text-zinc-900" data-testid="checkout-venue-terms-link">
                {venue?.name || "our"} Terms
              </a>{" "}
              &amp;{" "}
              <a href={`/v/${slug}/privacy`} target="_blank" rel="noreferrer" className="underline hover:text-zinc-900" data-testid="checkout-venue-privacy-link">
                Privacy Policy
              </a>.
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}

// -------- Location map --------
// Lightweight OpenStreetMap iframe embed (no API keys, no npm deps).
// If lat/lon aren't stored on the event we geocode client-side once via Nominatim as a fallback.
function EventLocationMap({ location, lat, lon }) {
  const [coords, setCoords] = useState(lat && lon ? { lat, lon } : null);

  useEffect(() => {
    if (coords || !location || location.trim().length < 3) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(location)}`,
          { headers: { "Accept-Language": "en" } }
        );
        const data = await r.json();
        if (!cancelled && Array.isArray(data) && data[0]) {
          setCoords({ lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) });
        }
      } catch (e) { /* offline / rate-limited — silently skip */ }
    })();
    return () => { cancelled = true; };
  }, [location, coords]);

  if (!location) return null;
  if (!coords) return null;

  // Small bbox around the point for the iframe view (~0.01deg ≈ 1km).
  const d = 0.005;
  const bbox = [coords.lon - d, coords.lat - d, coords.lon + d, coords.lat + d].join(",");
  const embedUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${coords.lat},${coords.lon}`;
  const linkUrl = `https://www.openstreetmap.org/?mlat=${coords.lat}&mlon=${coords.lon}#map=16/${coords.lat}/${coords.lon}`;

  return (
    <div className="mt-6 rounded-xl overflow-hidden border border-zinc-200" data-testid="event-location-map">
      <iframe
        title={`Map showing ${location}`}
        src={embedUrl}
        width="100%"
        height="220"
        loading="lazy"
        style={{ border: 0 }}
      />
      <div className="px-3 py-2 text-xs bg-zinc-50 border-t border-zinc-200 flex items-center justify-between gap-3">
        <span className="truncate">{location}</span>
        <a
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 shrink-0"
        >
          View larger →
        </a>
      </div>
    </div>
  );
}
